import { useTranslation } from 'react-i18next';
import styles from './../alert.module.scss';
import {
  auth,
  errorParser,
  FirebaseService,
  httpService,
  IconWrapper,
  REGEX,
} from '@flexo/general';
import { useRef, useState } from 'react';
import Textfield from '../../textfield/textfield';
import Button from '../../button/button';
import { Player } from '@lottiefiles/react-lottie-player';
import { ALERT, KPI, METADATA, MODAL, USER } from '@flexo/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ENV = (import.meta as any).env;
const loader = '/loaderLottie.json';

interface AlertConfirmCredentialsMobileProps {
  type: 'credentials' | 'delete';
}

export default function AlertConfirmCredentialsMobile({ 
  type = 'credentials' 
}: AlertConfirmCredentialsMobileProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const alertState = useSelector((state: any) => state.alert);
  const userState = useSelector((state: any) => state.user);
  const navigate = useNavigate();

  // Get the appropriate translation key based on type
  const translationKey = type === 'delete' ? 'banners.confirm_delete_account' : 'banners.confirm_credentials_changes';
  const bannerContent = t(translationKey, {
    returnObjects: true,
  }) as any;

  const formRef = useRef<any>(null);

  const [alertContent, setAlertContent] = useState<any>(
    t('alerts.wrong_request_data', { returnObjects: true })
  );
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [formError, setFormError] = useState<boolean>(false);
  const [fieldVisible, setFieldVisible] = useState<boolean>(false);
  const [sendingForm, setSendingForm] = useState<boolean>(false);
  const [showPasswordReset, setShowPasswordReset] = useState<boolean>(false);
  const [passwordField, setPasswordField] = useState<string>();
  const [icon, setIcon] = useState('visibility');

  const [inputType, setInputType] = useState({
    password: 'password',
    confirmPassword: 'password',
  });

  async function handleInputChange(event: any) {
    if (showAlert === true && formError === true) {
      setShowAlert(false);
      setShowPasswordReset(false);
    }
    setPasswordField(event.target.value);

    setFormError(false);
  }

  async function manageAlertCallBack() {
    let result: any = null;
    if (alertState?.alert?.callback) {
      result = await alertState.alert.callback();

      dispatch(ALERT.ResetAlert({} as any));
    }

    if (typeof result === 'number' && result > 200) {
      setFormError(true);

      if (result === 422) {
        setAlertContent(
          t('alerts.wrong_request_data', { returnObjects: true })
        );
      }

      setShowAlert(true);
    } else if (result === null) {
      dispatch(ALERT.ResetAlert({} as any));
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_UNEXPECTED_ERROR',
          },
        } as any)
      );
    }
  }

  const togglePasswordVisibility = (name: string) => {
    setInputType((prevState) => ({
      ...prevState,
      [name]: prevState[name] === 'password' ? 'text' : 'password',
    }));
    setIcon(icon === 'visibility' ? 'hide-visibility' : 'visibility');
  };

  async function checkPasswordValidity() {
    setSendingForm(true);

    // Build the data object conditionally based on type
    const requestData: any = {
      email:
        userState?.email ||
        userState?.user?.email ||
        auth?.currentUser?.email,
      password: passwordField,
    };

    // Only include tenant_id for delete account type
    if (type === 'delete') {
      requestData.tenant_id = ENV.VITE_APP_TENANT;
    }

    const response = await httpService
      .post({
        url: `${ENV.VITE_APP_FIREBASE_CUSTOM_TOKEN}`,
        data: requestData,
      })
      .then((resp: any) => ({
        status: resp.status,
        statusText: resp?.statusText || 'Unknown status text',
        message: resp?.data?.message || 'Unknown message',
      }))
      .catch((error) => {
        // console.warn(error);
        return {
          status: error?.response?.status || 500,
          statusText: error?.response?.statusText || 'Unknown error',
          ...errorParser(error),
        };
      });

    if (response.status === 200) {
      await manageAlertCallBack();
    } else {
      setShowAlert(false);
      setFormError(true);
      setShowPasswordReset(true);
    }

    setSendingForm(false);
    setPasswordField('');
  }

  const onCancel = () => {
    dispatch(ALERT.ResetAlert({} as any));
    setPasswordField('');
  };

  const onClickLogOut = async () => {
    const firebaseService = new FirebaseService();

    // Close both modal and alert immediately
    dispatch(MODAL.CloseModal());
    dispatch(ALERT.ResetAlert({} as any));

    try {
      // First, set flag to redirect to forgot password before doing any logout operations
      // This ensures the flag is set before auth state changes
      localStorage.setItem('redirectToForgotPassword', 'true');

      // Signal that this is an intentional logout (for forgot password)
      // This helps prevent session expired alerts
      dispatch(USER.SetLogout());

      // Pre-navigate to forgot password before signout operations
      // This helps avoid the login page flicker
      navigate('/forgot-password-mobile');

      // Clear app state
      dispatch(KPI.ResetKpiApiStore());
      dispatch(METADATA.ResetMetadata());

      // Get token but don't show errors if it fails
      let IDtoken;
      try {
        IDtoken = await firebaseService.getIdToken();
      } catch (error) {
        // Ignore token errors during logout process
      }

      if (IDtoken) {
        // Sign out from Firebase and backend first
        await httpService
          .post({
            url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/signout`,
            idToken: IDtoken,
            data: {},
          })
          .catch(() => {
            // Ignore errors during signout
          });
      }

      // Sign out from Firebase regardless of whether we got a token
      await firebaseService.signOut().catch(() => {
        // Ignore signout errors
      });
    } catch (error) {
      // Continue even if there's an error with signout
    } finally {
      // Reset any pending alerts to prevent session expired messages
      dispatch(ALERT.ResetAlert({} as any));
    }
  };

  // Get the appropriate ID and CSS classes based on type
  const componentId = type === 'delete' ? 'alert-confirm-delete-account-mobile' : 'alert-confirm-credentials-changes-mobile';
  const formId = type === 'delete' ? 'alert-confirm-delete-account-mobile-form' : 'alert-confirm-credentials-changes-mobile-form';
  const confirmButtonClass = type === 'delete' ? 'Alert__Footer__Actions__Mobile__Button__Delete' : 'Alert__Footer__Actions__Mobile__Button__Confirm';

  return (
    <div>
      <div
        id={componentId}
        className={`${styles.Alert__Confirm__Credentials__Changes__Mobile} ${styles.Alert}`}
      >
        <div className={styles.Alert__Title}>{bannerContent.title}</div>
        <div className={`paragraph ${styles.Alert__Content}`}>
          {bannerContent.description}
        </div>

        <form
          id={formId}
          className={styles.Alert__Content__Form}
        >
          <div className={styles.Alert__Content__Form__Input} ref={formRef}>
            <Textfield
              variant="containedLabel"
              name={bannerContent.inputName}
              label={bannerContent.inputLabel}
              type={inputType.password}
              value={passwordField}
              placeholder={bannerContent.inputPlaceholder}
              error={formError}
              onPaste={(e) => false}
              onCopy={(e) => false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="off"
              onChange={(ev: any) => handleInputChange(ev)}
              icon={icon}
              onIconClick={togglePasswordVisibility}
              errorDescription={
                formError ? 'Inserisci una password valida' : undefined
              }
            />
          </div>
        </form>

        <div className={styles.Alert__Content}>
          <span className={styles.Alert__Content__Link} onClick={onClickLogOut}>
            Forgot password?
          </span>
          <span>
            {' '}
            This will log out and reset your password with your email.
          </span>
        </div>

        <div className={styles.Alert__Footer__Actions__Mobile}>
          <button
            className={`${styles.Alert__Footer__Actions__Mobile__Button} ${styles.Alert__Footer__Actions__Mobile__Button__Dismiss}`}
            onClick={() => onCancel()}
          >
            {bannerContent.dismiss}
          </button>

          <button
            className={`${styles.Alert__Footer__Actions__Mobile__Button} ${styles[confirmButtonClass]}`}
            onClick={() => checkPasswordValidity()}
          >
            {sendingForm ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{ height: 20, transform: 'scale(0.7)' }}
              />
            ) : (
              bannerContent.confirm
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export { AlertConfirmCredentialsMobile };